/**
 * THE ONE PLACE A CAUGHT ERROR BECOMES WHAT A PERSON READS (SCRUM-965). Owner, 31.10.2026: «надо чинить вот это всё с почтой. Чтобы
 * явно было понятно что надо зарегиться»; the critic's second pass the same day: the fix is general, not only for the email —
 * «ни одного сырого API … {…} человеку».
 *
 * Before this, every page wrote `err instanceof Error ? err.message : String(err)` into its status line, and for a server refusal that
 * message is the transport's own text — `API 403 Forbidden: {"statusCode":403,"error":"email_not_verified",…}` (`api/http.ts#parseOrThrow`).
 * Seen on dev in Crew (roles, contacts), Rider from PDF and the worker's crew link page; on a phone the unbroken JSON also pushed the
 * page past the right edge.
 *
 * The rule, by the refusal's CODE (the body's `error` or `code`):
 *   - `email_not_verified` → the sentence about confirming the email and where to resend the letter;
 *   - `pdf_not_configured` → PDF recognition is not available right now;
 *   - a 4xx whose body carries the API's own sentence (`message`) → that sentence, without the transport around it: the API writes
 *     those for a person («an email or a phone is required»), and before this they were shown inside the raw body — measured on the
 *     stand 01.11.2026, replacing them with the general sentence lost the reason and said something false. Nest's own stock words
 *     («Forbidden resource», «… Exception», «Internal server error», or the same word as `error`) are not a sentence;
 *   - any other refusal, a 5xx, and the network → one short general sentence.
 * The raw text goes to the console (`console.warn`), never to the page. A sentence the PRODUCT threw itself (a plain `Error` whose text
 * is not a server refusal) is kept: those are ours and already worded for a person.
 */
import { ApiError } from "../api/http";
import { t } from "../i18n";

/** What the transport writes for a refusal: `API <status> <statusText>: <body>` (also built outside `ApiError`, e.g. the warehouse's). */
const REFUSAL_TEXT = /^API \d{3}\b/;

function isServerRefusal(err: unknown): err is Error {
  return err instanceof ApiError || (err instanceof Error && REFUSAL_TEXT.test(err.message));
}

/** The code the server tagged its refusal with — the body's `error`, else its `code`; `null` when there is no JSON body or no code. */
export function apiErrorCode(err: unknown): string | null {
  if (!isServerRefusal(err)) return null;
  const at = err.message.indexOf("{");
  if (at === -1) return null;
  try {
    const body = JSON.parse(err.message.slice(at)) as { error?: unknown; code?: unknown };
    if (typeof body.error === "string" && body.error) return body.error;
    if (typeof body.code === "string" && body.code) return body.code;
    return null;
  } catch {
    return null;
  }
}

/** Nest's stock words for a status — not a sentence anyone wrote for a person. */
const STOCK_MESSAGE = /^(forbidden resource|internal server error)$|exception$/i;

/** The API's own sentence in a 4xx body (`message`, or the first of a validator's list); `null` when there is none worth showing. */
function apiSentence(err: Error): string | null {
  const status = err instanceof ApiError ? err.status : Number(/^API (\d{3})\b/.exec(err.message)?.[1]);
  if (!(status >= 400 && status < 500)) return null;
  const at = err.message.indexOf("{");
  if (at === -1) return null;
  try {
    const body = JSON.parse(err.message.slice(at)) as { message?: unknown; error?: unknown };
    const raw = Array.isArray(body.message) ? body.message.filter((m): m is string => typeof m === "string").join("; ") : body.message;
    if (typeof raw !== "string") return null;
    const text = raw.trim();
    if (!text || STOCK_MESSAGE.test(text) || text === body.error) return null;
    return text;
  } catch {
    return null;
  }
}

/** The sentence for `err`; the raw text of a refusal or a network failure is written to the console instead. */
export function humanErrorText(err: unknown): string {
  if (isServerRefusal(err)) {
    console.warn("[api refusal]", err.message);
    const code = apiErrorCode(err);
    if (code === "email_not_verified") return t("errors.emailNotVerified");
    if (code === "pdf_not_configured") return t("errors.pdfNotConfigured");
    return apiSentence(err) ?? t("errors.general");
  }
  // The browser's own words for a dropped connection («Failed to fetch», «Load failed», «NetworkError …») are not ours either.
  if (err instanceof TypeError) {
    console.warn("[network]", err.message);
    return t("errors.general");
  }
  if (err instanceof Error && err.message) return err.message;
  console.warn("[error]", err);
  return t("errors.general");
}
